import React, { useState, useEffect } from 'react';
import {
  getAuditLogListAPI,
  AuditLog,
  AuditLogSearchRequest,
} from '../../../services/adminApi';
import { ToastContainer } from '../../../components/Toast';
import { useToast } from '../../../hooks/useToast';
import styles from './AuditLogs.module.css';

const AuditLogs: React.FC = () => {
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const { toasts, removeToast, showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [entityType, setEntityType] = useState('All Entities');
  const [actionType, setAction] = useState('All Actions');
  const [limit, setLimit] = useState(100);

  // Format timestamp without locale conversion - displays exact time
  const formatDateTime = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      const period = date.getHours() >= 12 ? 'PM' : 'AM';
      const hours12 = String(date.getHours() % 12 || 12).padStart(2, '0');
      return `${day}/${month}/${year} ${hours12}:${minutes}:${seconds} ${period}`;
    } catch {
      return dateString;
    }
  };

  // Load data on component mount and when filters change
  useEffect(() => {
    loadData();
  }, [entityType, actionType, limit, ]);

  const loadData = async () => {
    try {
      setLoading(true);
      const searchRequest: AuditLogSearchRequest = {
        entityType: entityType === 'All Entities' ? '' : entityType,
        actionType: actionType === 'All Actions' ? '' : actionType,
        pageNo: 1,
        pageSize: limit,
        searchText: ''
      };
      const response = await getAuditLogListAPI(searchRequest);
      if (response.isSuccess && response.data) {
        setAuditLogs(response.data);
      } else {
        showError(response.message || 'Failed to load audit logs');
      }
    } catch (error) {
      console.error('Failed to load audit logs:', error);
      showError('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const entityTypeOptions = [
    'All Entities',
    'Party',
    'Party Account',
    'Token',
    'Dollar Account',
    'Internal Account',
    'Payment Mode',
    'User',
  ];

  const actionOptions = [
    'All Actions',
    'Create',
    'Update',
    'Delete',
    'View',
    'Login',
    'Logout',
    'Change Password',
  ];

  const limitOptions = [50, 100, 200, 500];

  return (
    <div className={styles.container}>
      {/* Toast Container */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1 className={styles.title}>Audit Logs</h1>
          <p className={styles.subtitle}>Track all system activities and changes</p>
        </div>
      </div>

      {/* Filters */}
      <div className={styles.filterSection}>
        <div className={styles.filterGroup}>
          <label htmlFor="entityType" className={styles.filterLabel}>
            Entity Type
          </label>
          <select
            id="entityType"
            value={entityType}
            onChange={(e) => setEntityType(e.target.value)}
            className={styles.filterSelect}
          >
            {entityTypeOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.filterGroup}>
          <label htmlFor="actionType" className={styles.filterLabel}>
            Action
          </label>
          <select
            id="actionType"
            value={actionType}
            onChange={(e) => setAction(e.target.value)}
            className={styles.filterSelect}
          >
            {actionOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.filterGroup}>
          <label htmlFor="limit" className={styles.filterLabel}>
            Limit
          </label>
          <select
            id="limit"
            value={limit}
            onChange={(e) => setLimit(parseInt(e.target.value))}
            className={styles.filterSelect}
          >
            {limitOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className={styles.tableContainer}>
        {loading ? (
          <div className={styles.loading}>Loading audit logs...</div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>TIMESTAMP</th>
                <th>USER</th>
                <th>ACTION</th>
                <th>ENTITY</th>
                <th>REMARKS</th>
              </tr>
            </thead>
            <tbody>
              {auditLogs.length === 0 ? (
                <tr>
                  <td colSpan={5} className={styles.emptyState}>
                    <div className={styles.emptyIcon}>⏱️</div>
                    <div>No audit logs found</div>
                  </td>
                </tr>
              ) : (
                auditLogs.map((log, index) => (
                  <tr key={index}>
                    <td className={styles.timestampCell}>
                      {log.createdDate
                        ? formatDateTime(log.createdDate)
                        : '-'}
                    </td>
                    <td className={styles.userCell}>{log.creatorName || '-'}</td>
                    <td>
                      <span className={`${styles.actionBadge} ${styles[log.actionType?.toLowerCase() || '']}`}>
                        {log.actionType?.toLowerCase() || '-'}
                      </span>
                    </td>
                    <td className={styles.entityCell}>
                      <span className={styles.entityBadge}>
                        {log.entityType || '-'}
                      </span>
                    </td>
                    <td className={styles.remarksCell}>{log.remarks || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AuditLogs;
